import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import Script from 'next/script'
import { getStoredCookieConsent } from 'src/functions/cookieConsent'
import { isNewsletterPrivatePath, redactNewsletterUrl } from 'src/functions/newsletterPrivacy'

type GtagCommand = [string, ...unknown[]]

declare global {
  interface Window {
    dataLayer?: GtagCommand[]
    gtag?: (...args: GtagCommand) => void
  }
}

const scriptId = 'google-analytics-script'
const bootstrapScriptId = 'google-analytics-bootstrap'

const getPageViewParams = (url?: string) => {
  const path = redactNewsletterUrl(url || window.location.pathname + window.location.search)

  return {
    page_title: document.title,
    page_location: `${window.location.origin}${path}`,
    page_path: path,
    page_referrer: redactNewsletterUrl(document.referrer || ''),
  }
}

const googleConsent = (analyticsStorage: 'granted' | 'denied') => ({
  analytics_storage: analyticsStorage,
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
})

const ensureGtag = () => {
  window.dataLayer = window.dataLayer || []
  window.gtag = window.gtag || ((...args: GtagCommand) => {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(args)
  })
}

export function GoogleAnalytics({ measurementId }: { measurementId: string }) {
  if (!measurementId) return null

  return <>
    <Script
      id={bootstrapScriptId}
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{
        __html: `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          window.gtag = window.gtag || gtag;
          gtag('consent', 'default', {
            analytics_storage: 'denied',
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied'
          });
          gtag('js', new Date());
          gtag('config', ${JSON.stringify(measurementId)}, { send_page_view: false });
        `,
      }}
    />
    <Script
      id={scriptId}
      strategy="afterInteractive"
      src={`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`}
    />
  </>
}

type GoogleAnalyticsPageViewProps = {
  measurementId: string
  enabled: boolean
  trackInitialImmediately?: boolean
}

export function GoogleAnalyticsPageView({
  measurementId,
  enabled,
  trackInitialImmediately = true,
}: GoogleAnalyticsPageViewProps) {
  const router = useRouter()
  const initialTracked = useRef(false)
  const lastTrackedPath = useRef<string | null>(null)

  const trackPageView = (url?: string) => {
    if (isNewsletterPrivatePath(url || window.location.pathname)) return
    const params = getPageViewParams(url)
    window.gtag?.('config', measurementId, params)
    lastTrackedPath.current = params.page_path
  }

  useEffect(() => {
    if (typeof window === 'undefined' || !measurementId) return

    ensureGtag()
    const storedConsent = getStoredCookieConsent()
    const analyticsAllowed = enabled || storedConsent === 'all'
    window.gtag?.('consent', 'update', googleConsent(analyticsAllowed ? 'granted' : 'denied'))

    if (trackInitialImmediately && !initialTracked.current) {
      initialTracked.current = true
      trackPageView()
      return
    }

    if (!analyticsAllowed) return

    const currentPath = window.location.pathname + window.location.search
    if (lastTrackedPath.current === currentPath) return

    trackPageView()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, measurementId, trackInitialImmediately])

  useEffect(() => {
    if (typeof window === 'undefined' || !measurementId || !enabled) return

    let timeout: number | undefined
    ensureGtag()

    const trackPageView = (url: string) => {
      if (timeout) window.clearTimeout(timeout)
      timeout = window.setTimeout(() => {
        if (isNewsletterPrivatePath(url)) return
        const params = getPageViewParams(url)
        window.gtag?.('config', measurementId, params)
        lastTrackedPath.current = params.page_path
      }, 200)
    }

    router.events.on('routeChangeComplete', trackPageView)

    return () => {
      router.events.off('routeChangeComplete', trackPageView)
      if (timeout) window.clearTimeout(timeout)
    }
  }, [enabled, measurementId, router.events])

  return null
}
